import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useOtterfolioPositionDetail } from "../../hooks/otterfolio";
import type { DetailPeriod } from "../../lib/otterfolio-detail";
import { ToggleGroup } from "./ToggleGroup";
import { OTTERFOLIO_THEME } from "./theme";

interface Props {
  positionId: string | null;
  onClose: () => void;
}

function fmtEur(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function fmtPct(pct: number, digits = 1): string {
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(digits)} %`;
}

function fmtSharePct(pct: number, digits = 1): string {
  return `${pct.toFixed(digits)} %`;
}

function fmtDate(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

export function PositionDetailModal({ positionId, onClose }: Props) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<DetailPeriod>("90d");
  const { data } = useOtterfolioPositionDetail(positionId ?? "", period, positionId !== null);

  useEffect(() => {
    if (positionId === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [positionId, onClose]);

  if (positionId === null) return null;

  const perfColor =
    (data?.gainLossPercent ?? 0) >= 0 ? OTTERFOLIO_THEME.primary : OTTERFOLIO_THEME.highlight;
  const groupColor = data?.category === "FELS" ? OTTERFOLIO_THEME.fels : OTTERFOLIO_THEME.rakete;
  const categoryEmoji = data?.category === "FELS" ? "🪨" : data?.category === "RAKETE" ? "🚀" : "";

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: ESC-Handler übernimmt Keyboard-Close (document listener)
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-Guard, kein Keyboard-Äquivalent nötig */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: OTTERFOLIO_THEME.bg,
          color: OTTERFOLIO_THEME.text,
          border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
          borderRadius: 12,
          maxWidth: 720,
          width: "100%",
          padding: 20,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        {!data ? (
          <div style={{ color: OTTERFOLIO_THEME.textMuted }}>
            {t("otterfolio.modal.loading", { defaultValue: "Lade …" })}
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                paddingBottom: 12,
                borderBottom: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{data.name}</div>
                <div style={{ color: OTTERFOLIO_THEME.textMuted, fontSize: 10.5 }}>
                  {categoryEmoji} {data.category ?? ""} · {data.isin ?? ""}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{fmtEur(data.valueCents)}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: perfColor }}>
                  {fmtPct(data.gainLossPercent ?? 0)}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                marginTop: 12,
              }}
            >
              <div>
                <div
                  style={{
                    color: OTTERFOLIO_THEME.textMuted,
                    fontSize: 10,
                    textTransform: "uppercase",
                  }}
                >
                  {t("otterfolio.modal.value", { defaultValue: "Wert" })}
                </div>
                <div style={{ fontWeight: 700 }}>{fmtEur(data.valueCents)}</div>
              </div>
              <div>
                <div
                  style={{
                    color: OTTERFOLIO_THEME.textMuted,
                    fontSize: 10,
                    textTransform: "uppercase",
                  }}
                >
                  {t("otterfolio.modal.depotShare", { defaultValue: "Anteil Depot" })}
                </div>
                <div style={{ fontWeight: 700 }}>{fmtSharePct(data.depotSharePercent)}</div>
              </div>
              <div>
                <div
                  style={{
                    color: OTTERFOLIO_THEME.textMuted,
                    fontSize: 10,
                    textTransform: "uppercase",
                  }}
                >
                  {t("otterfolio.modal.avgCost", { defaultValue: "Ø Einstand" })}
                </div>
                <div style={{ fontWeight: 700 }}>{fmtEur(data.avgCostCents)}</div>
              </div>
            </div>

            <div style={{ height: 180, marginTop: 16 }}>
              <ResponsiveContainer>
                <LineChart data={data.chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke={OTTERFOLIO_THEME.cardBorder} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: OTTERFOLIO_THEME.textMuted }}
                    tickFormatter={fmtDate}
                  />
                  <YAxis yAxisId="left" hide />
                  <YAxis yAxisId="right" orientation="right" hide />
                  <Tooltip
                    contentStyle={{
                      background: OTTERFOLIO_THEME.cardBg,
                      border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
                    }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="groupIndexPercent"
                    stroke={groupColor}
                    strokeOpacity={0.4}
                    strokeDasharray="4 3"
                    strokeWidth={1.2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="positionPriceEurCents"
                    stroke={OTTERFOLIO_THEME.primary}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ textAlign: "right", marginTop: 6 }}>
              <ToggleGroup
                options={[
                  { key: "30d", label: "30T" },
                  { key: "90d", label: "90T" },
                  {
                    key: "since-buy",
                    label: t("otterfolio.modal.sinceBuy", { defaultValue: "Seit Kauf" }),
                  },
                ]}
                active={period}
                onChange={(v) => setPeriod(v as DetailPeriod)}
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  color: OTTERFOLIO_THEME.fels,
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                {t("otterfolio.modal.transactionsSinceStart", {
                  defaultValue: "Transaktionen seit 01.07.",
                })}
              </div>
              {data.transactions.length === 0 ? (
                <div style={{ color: OTTERFOLIO_THEME.textMuted, fontSize: 11 }}>
                  {t("otterfolio.modal.transactionsEmpty", {
                    defaultValue: "Noch keine Transaktionen im Otterfolio-Fenster",
                  })}
                </div>
              ) : (
                data.transactions.map((tx, i) => (
                  <div
                    key={`${tx.date}-${i}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "5px 0",
                      borderBottom: `1px solid ${OTTERFOLIO_THEME.cardBg}`,
                      fontSize: 11,
                    }}
                  >
                    <span style={{ color: OTTERFOLIO_THEME.textMuted }}>
                      {fmtDate(tx.date)} · {tx.type}
                      {tx.shares !== null ? ` · ${tx.shares}` : ""}
                    </span>
                    <span
                      style={{
                        color:
                          tx.amountCents >= 0
                            ? OTTERFOLIO_THEME.primary
                            : OTTERFOLIO_THEME.highlight,
                        fontWeight: 700,
                      }}
                    >
                      {fmtEur(tx.amountCents)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
